import { motion } from 'framer-motion';
import React from 'react';

import { cn } from './cn';

export interface ToggleProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  inlineLabel?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked = false,
      onChange,
      label,
      inlineLabel = false,
      disabled = false,
      className,
      id,
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : internalChecked;

    const handleToggle = () => {
      if (disabled) return;
      const newValue = !checked;
      if (!isControlled) setInternalChecked(newValue);
      onChange?.(newValue);
    };

    const generatedId = React.useId();
    const toggleId = id || generatedId;

    return (
      <div className={cn('flex items-center gap-2.5', inlineLabel && 'flex-row-reverse justify-end', className)}>
        <button
          ref={ref}
          id={toggleId}
          role="switch"
          type="button"
          aria-checked={checked}
          disabled={disabled}
          onClick={handleToggle}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-500 focus-visible:ring-offset-2',
            checked ? 'bg-primary-500' : 'bg-gray-300',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <motion.span
            className="pointer-events-none block h-5 w-5 rounded-full bg-surface shadow-sm ring-0"
            animate={{ x: checked ? 20 : 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </button>
        {label && (
          <label
            htmlFor={toggleId}
            // The label stays readable when the control is disabled — only the track dims. Dimming
            // the text too (the old `opacity-50`) dropped it below legibility on dark surfaces, and
            // the user still needs to read what the disabled switch would do.
            className={cn(
              'text-sm text-text-secondary select-none',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);
Toggle.displayName = 'Toggle';
